import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Modal, Form, Select, InputNumber, DatePicker, Alert, Checkbox, notification } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { MemberProfile } from '../../../model/members/MemberProfile';
import { MembershipCategory } from '../../../model/members/MembershipCategory';
import { MembershipCategoryRow } from '../../../model/members/MembershipCategoryRow';
import {
    Promotion,
    PROMOTION_TYPE_MEMBERSHIP,
    asMembershipConfig,
} from '../../../model/pointofsale/Promotion';
import { MemberPromotion } from '../../../model/pointofsale/MemberPromotion';
import { PromotionPreview, PromotionPaymentStatus } from '../../../model/pointofsale/PromotionPreview';
import { ApplyPromotionRequest } from '../../../requests/pointofsale/ApplyPromotionRequest';
import { BillingSettings } from '../../../model/client/ClientSettings';
import { PromotionReadPermissions } from '../../../model/auth/Permissions';
import { useMember } from '../../../common/member/MemberContext';
import { usePromotionService } from '../../../services/pointofsale/PromotionServiceProvider';
import { useMemberService } from '../../../services/members/MemberServiceProvider';
import { useReceiptPrinterService } from '../../../services/printing/ReceiptPrinterServiceProvider';
import { useClientConfig } from '../../../common/client/ClientConfigProvider';
import { useCurrencySymbol } from '../../../hooks/useFormatters';
import { promotionValidTo, promotionFee } from '../../../utils/billingCycle';
import { toDateOnly, fromDateOnly } from '../../../utils/DateOnly';
import { processPromotionReceipt } from '../../../utils/membershipTransactionUtils';
import { mostRecentApplication, lastApplicationOfPromotion } from '../../../utils/promotionHistory';
import { RadioGroup } from '../../../ui/primitives/Radio';
import MembershipCategorySelector from '../MembershipCategorySelector';
import PromotionConfirmationPanel from './shared/PromotionConfirmationPanel';

const DATE_FORMAT = 'DD/MM/YYYY';

// MembershipCategorySelector wired to a form field holding the category NAME (forwards id for label
// association), translating its object onChange back to the name.
const CategorySelect: React.FC<{
    value?: string;
    onChange?: (value: string) => void;
    categories: MembershipCategory[];
    id?: string;
}> = ({ value, onChange, categories, id }) => (
    <MembershipCategorySelector
        id={id}
        membershipCategories={categories}
        value={value}
        onChange={(category) => onChange?.(category?.name ?? '')}
    />
);

interface FieldsContext {
    categories: MembershipCategory[];
    currencySymbol: string;
}

// A promotion type's apply behaviour. Adding a future type means adding one entry to APPLY_SPECS —
// the modal renders its fields, populates them on select, recomputes derived fields, and builds the
// apply-request overrides generically.
interface ApplyTypeSpec {
    type: string;
    renderFields: (context: FieldsContext) => React.ReactNode;
    // Form values applied when a promotion of this type is selected.
    toFormValues: (promotion: Promotion, categories: MembershipCategoryRow[], billing?: BillingSettings)
        => Record<string, unknown>;
    // Derived fields to recompute when valid-from changes (Membership: the valid-to snap).
    recomputeOnValidFrom: (validFrom: Dayjs, promotion: Promotion, billing?: BillingSettings)
        => Record<string, unknown>;
    // Form values -> apply-request overrides. Returns an error message when a value can't resolve.
    buildOverrides: (values: Record<string, unknown>, categories: MembershipCategoryRow[])
        => { overrides: Partial<ApplyPromotionRequest> } | { error: string };
    // Optional extra confirmation rows, for a type whose preview carries data the shared panel
    // can't infer. Membership needs none — the shared panel covers the whole plan.
    renderConfirmationExtras?: (preview: PromotionPreview) => React.ReactNode;
}

const membershipApplySpec: ApplyTypeSpec = {
    type: PROMOTION_TYPE_MEMBERSHIP,
    renderFields: ({ categories, currencySymbol }) => (
        <>
            <Form.Item
                name={['config', 'memberCategoryName']}
                label="Membership category"
                rules={[{ required: true, message: 'Please select a membership category' }]}
            >
                <CategorySelect categories={categories} />
            </Form.Item>
            <Form.Item
                name={['config', 'amount']}
                label={`Amount (${currencySymbol})`}
                rules={[
                    { required: true, message: 'Please enter the amount' },
                    { type: 'number', min: 0, message: 'Amount must be 0 or greater' },
                ]}
            >
                <InputNumber
                    inputMode="decimal"
                    min={0}
                    step={0.01}
                    precision={2}
                    style={{ width: '100%' }}
                    addonBefore={currencySymbol}
                />
            </Form.Item>
            <Form.Item
                name={['config', 'validFrom']}
                label="Valid from"
                rules={[{ required: true, message: 'Please choose a start date' }]}
            >
                <DatePicker format={DATE_FORMAT} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
                name={['config', 'validTo']}
                label="Valid to"
                rules={[{ required: true, message: 'Please choose an end date' }]}
            >
                <DatePicker format={DATE_FORMAT} style={{ width: '100%' }} />
            </Form.Item>
        </>
    ),
    toFormValues: (promotion, categories, billing) => {
        const membership = asMembershipConfig(promotion);
        const validFrom = dayjs().startOf('day');
        const category = categories.find((c) => c.id === membership?.memberCategoryId);
        if (!membership) {
            return { memberCategoryName: category?.name ?? '', amount: 0, validFrom, validTo: validFrom };
        }
        return {
            memberCategoryName: category?.name ?? '',
            // Display default — the apply-promotion preview recomputes both fee and valid-to server-side.
            amount: promotionFee(
                membership.fixedFee, category?.price ?? 0, category?.paymentFrequency ?? 'Monthly',
                validFrom, membership.durationMonths, membership.snapToBillingCycle, membership.proRata, billing),
            validFrom,
            validTo: promotionValidTo(
                validFrom, membership.durationMonths, membership.snapToBillingCycle, billing),
        };
    },
    recomputeOnValidFrom: (validFrom, promotion, billing) => {
        const membership = asMembershipConfig(promotion);
        return {
            validTo: promotionValidTo(
                validFrom, membership?.durationMonths ?? 0, membership?.snapToBillingCycle ?? true, billing),
        };
    },
    buildOverrides: (values, categories) => {
        const config = (values.config ?? {}) as Record<string, unknown>;
        const category = categories.find((c) => c.name === String(config.memberCategoryName ?? ''));
        if (!category) {
            return { error: 'Please select a valid membership category.' };
        }
        return {
            overrides: {
                amount: Number(config.amount ?? 0),
                memberCategoryId: category.id,
                validFrom: toDateOnly(config.validFrom as Dayjs),
                validTo: toDateOnly(config.validTo as Dayjs),
            },
        };
    },
};

const APPLY_SPECS: Record<string, ApplyTypeSpec> = {
    [membershipApplySpec.type]: membershipApplySpec,
};

interface AddPromotionModalProps {
    member: MemberProfile;
    // Called after a promotion is successfully applied (member changed + fee charged).
    onApplied?: () => void;
    // Full-width trigger button (embedded/quick-actions layout).
    block?: boolean;
}

// "Add Promotion" member action (FORT-316): self-contained trigger + modal. Picks an active
// promotion, shows/edits the type-specific fields, then (FORT-318) confirms the resolved plan —
// fee, category change, date changes — before charging anything and printing a receipt.
const AddPromotionModal: React.FC<AddPromotionModalProps> = ({ member, onApplied, block }) => {
    const promotionService = usePromotionService();
    const memberService = useMemberService();
    const receiptPrinterService = useReceiptPrinterService();
    const { billing } = useClientConfig();
    const currencySymbol = useCurrencySymbol();
    const { permissions } = useMember();
    const [form] = Form.useForm();

    // Mirrors the API's CanReadPromotions policy. Applying is gated on members:update, but the
    // modal can't be filled without reading the promotions and categories first — so without
    // this permission the action is hidden rather than opening onto an empty dropdown (FORT-398).
    const canReadPromotions = PromotionReadPermissions.some((required) =>
        permissions?.permissions?.some((p) => p.name === required));

    const [open, setOpen] = useState(false);
    const [promotions, setPromotions] = useState<Promotion[]>([]);
    const [categories, setCategories] = useState<MembershipCategoryRow[]>([]);
    const [history, setHistory] = useState<MemberPromotion[]>([]);
    const [selectedPromotionId, setSelectedPromotionId] = useState<number | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [step, setStep] = useState<'edit' | 'confirm'>('edit');
    const [preview, setPreview] = useState<PromotionPreview | null>(null);
    const [isPreviewing, setIsPreviewing] = useState(false);
    // Whether the operator has hand-edited "Valid to". Until they do, the preview call omits it so
    // the server computes the billing-anchor snap itself — otherwise the locally-derived date would
    // always win and the confirmation panel could show something the apply wouldn't actually do.
    const [validToDirty, setValidToDirty] = useState(false);
    // Same treatment for the fee: until the operator edits it, the preview omits `amount` so the
    // server computes the pro-rata figure; a hand-edited amount is sent as-is.
    const [amountDirty, setAmountDirty] = useState(false);
    // Confirm-step opt-in: activate an inactive member off the back of the promotion (the promotion
    // already sets their category/validity window, so activation just flips status + isEnabled).
    const [activateMember, setActivateMember] = useState(false);
    // True when the promotion catalogue or the membership categories failed to load — most often
    // a 403 from a token the client believed could read them.
    const [loadFailed, setLoadFailed] = useState(false);

    const categoryOptions = useMemo<MembershipCategory[]>(
        () => categories.map((c) => ({
            name: c.name,
            amount: c.price,
            paymentFrequency: c.paymentFrequency,
            isJointMembership: c.isJointMembership,
            isVisible: c.isVisible,
        })),
        [categories],
    );

    const loadData = useCallback(async () => {
        // Settled, not all: a rejected sub-load used to reject the whole Promise.all, so the
        // modal rendered with an empty promotion picker and no explanation — the operator saw a
        // blank dropdown and no reason for it. Hiding the action for accounts that definitively
        // cannot read (above) does not cover this: a token can be refused at runtime by an API
        // the client believed would serve it, which is the divergence this bug WAS.
        const [promosResult, catsResult, histResult] = await Promise.allSettled([
            promotionService.getPromotions(),
            memberService.getMembershipCategoriesDetailed(),
            // History is only decorative (note + soft-block); its failure must not blank the modal.
            promotionService.getMemberPromotions(member.membershipId).catch(() => [] as MemberPromotion[]),
        ]);

        // Cleared on failure rather than left holding a previous open's data: an error alert
        // sitting above a populated dropdown is a worse signal than either alone.
        setPromotions(promosResult.status === 'fulfilled'
            ? promosResult.value.filter((p) => p.isActive)
            : []);
        setCategories(catsResult.status === 'fulfilled' ? catsResult.value : []);
        setHistory(histResult.status === 'fulfilled' ? histResult.value : []);

        // Either failure leaves the operator unable to choose a promotion, so both surface the
        // same blocking message rather than a silently empty form.
        setLoadFailed(promosResult.status === 'rejected' || catsResult.status === 'rejected');
    }, [promotionService, memberService, member.membershipId]);

    useEffect(() => {
        if (!open) return;
        setLoadFailed(false);
        loadData().catch(() => setLoadFailed(true));
    }, [open, loadData]);

    const selectedPromotion = promotions.find((p) => p.id === selectedPromotionId) ?? null;
    const activeSpec = selectedPromotion ? APPLY_SPECS[selectedPromotion.promotionType] : null;

    const handlePromotionSelect = (promotionId: number) => {
        setSelectedPromotionId(promotionId);
        setValidToDirty(false);
        setAmountDirty(false);
        const promotion = promotions.find((p) => p.id === promotionId);
        const spec = promotion ? APPLY_SPECS[promotion.promotionType] : null;
        if (promotion && spec) {
            form.setFieldsValue({ config: spec.toFormValues(promotion, categories, billing) });
        }
    };

    // Recompute derived display defaults (valid-to, pro-rata fee) when their inputs change, and
    // notice when the operator hand-edits valid-to or the amount.
    const handleValuesChange = (changed: Record<string, unknown>) => {
        const changedConfig = changed.config as Record<string, unknown> | undefined;
        if (!changedConfig) return;

        if ('validTo' in changedConfig) setValidToDirty(true);
        if ('amount' in changedConfig) setAmountDirty(true);

        if (!selectedPromotion || !activeSpec) return;

        // Valid-to derives from valid-from; the pro-rata fee derives from valid-from and the chosen
        // category. Only these two inputs trigger a recompute.
        const validFromChanged = 'validFrom' in changedConfig;
        const categoryChanged = 'memberCategoryName' in changedConfig;
        if (!validFromChanged && !categoryChanged) return;

        const current = form.getFieldValue('config') as Record<string, unknown>;
        const validFrom = (validFromChanged ? changedConfig.validFrom : current.validFrom) as Dayjs | null;
        if (!validFrom) return;

        const updates: Record<string, unknown> = {};

        // Valid-from moves the whole window: the recomputed valid-to is ours again, not the operator's.
        if (validFromChanged) {
            Object.assign(updates, activeSpec.recomputeOnValidFrom(validFrom, selectedPromotion, billing));
            setValidToDirty(false);
        }

        // Recompute the fee default when the window moves (which re-owns the amount) or the category
        // changes — but never clobber an amount the operator has hand-edited on a mere category change.
        const membership = asMembershipConfig(selectedPromotion);
        if (membership && (validFromChanged || !amountDirty)) {
            const category = categories.find((c) => c.name === String(current.memberCategoryName ?? ''));
            updates.amount = promotionFee(
                membership.fixedFee, category?.price ?? 0, category?.paymentFrequency ?? 'Monthly',
                validFrom, membership.durationMonths, membership.snapToBillingCycle, membership.proRata, billing);
            if (validFromChanged) setAmountDirty(false);
        }

        if (Object.keys(updates).length > 0) {
            form.setFieldsValue({ config: { ...current, ...updates } });
        }
    };

    const closeModal = () => {
        setOpen(false);
        setSelectedPromotionId(null);
        setStep('edit');
        setPreview(null);
        setValidToDirty(false);
        setAmountDirty(false);
        setActivateMember(false);
    };

    const paymentStatus = (): PromotionPaymentStatus =>
        (form.getFieldValue('paymentStatus') as PromotionPaymentStatus) ?? 'Completed';

    // Validate the form and turn it into apply-request overrides, or null when it can't resolve.
    const buildOverrides = async (): Promise<Partial<ApplyPromotionRequest> | null> => {
        let values: Record<string, unknown>;
        try {
            values = await form.validateFields();
        } catch {
            return null;
        }
        if (!activeSpec) return null;

        const built = activeSpec.buildOverrides(values, categories);
        if ('error' in built) {
            form.setFields([{ name: ['config', 'memberCategoryName'], errors: [built.error] }]);
            return null;
        }
        return { ...built.overrides, paymentStatus: paymentStatus() };
    };

    const handleContinue = async () => {
        const overrides = await buildOverrides();
        if (!overrides || !selectedPromotion) return;

        setIsPreviewing(true);
        try {
            const request: ApplyPromotionRequest = { promotionId: selectedPromotion.id, ...overrides };
            // Let the server snap the end date / compute the pro-rata fee unless the operator set them.
            if (!validToDirty) delete request.validTo;
            if (!amountDirty) delete request.amount;

            const resolved = await promotionService.previewPromotion(member.membershipId, request);
            // Put the server's date and fee back in the fields so going Back shows what will actually
            // apply, and the subsequent apply sends those same values. fromDateOnly, not dayjs — the
            // revived date is UTC midnight, and reading it locally would shift it a day west of Greenwich.
            form.setFieldsValue({
                config: {
                    ...form.getFieldValue('config'),
                    validTo: fromDateOnly(resolved.validTo),
                    amount: resolved.amount,
                },
            });
            setPreview(resolved);
            setStep('confirm');
        } catch {
            // The API client already surfaced the error; stay on step 1 so it can be corrected.
        } finally {
            setIsPreviewing(false);
        }
    };

    const handleConfirm = async () => {
        const overrides = await buildOverrides();
        // Validation messages render on the (currently hidden) form, so send the operator back to
        // where they can actually see them rather than leaving Confirm looking dead.
        if (!overrides || !selectedPromotion || !preview) {
            setStep('edit');
            return;
        }

        const status = paymentStatus();
        setIsProcessing(true);
        try {
            const request: ApplyPromotionRequest = { promotionId: selectedPromotion.id, ...overrides };
            const applied = await promotionService.applyPromotion(member.membershipId, request);

            notification.success({
                message: 'Promotion applied',
                description: `Applied "${selectedPromotion.name}" to ${member.name}.`,
            });

            // If the operator opted in, activate an inactive member off the back of the promotion.
            // The promotion has already set the resulting category and validity window server-side,
            // so re-fetch to carry that authoritative post-promotion state forward — spreading the
            // stale `member` would send its pre-promotion category and revert the change. Activation
            // then just flips status + isEnabled. Best effort — a failed activation must not read as
            // a failed promotion (the fee is already committed).
            if (activateMember && !member.isEnabled) {
                try {
                    const updated = await memberService.getMember(member.membershipId, false);
                    await memberService.update({
                        ...updated,
                        // fromDateOnly first: the revived window is UTC midnight, and reading it
                        // straight through toDateOnly would shift it a day west of Greenwich.
                        validFrom: toDateOnly(fromDateOnly(updated.validFrom)),
                        validTo: toDateOnly(fromDateOnly(updated.validTo)),
                        isEnabled: true,
                        status: 'Active',
                        changeType: 'Activate',
                    });
                } catch {
                    notification.warning({
                        message: 'Member not activated',
                        description: 'The promotion was applied, but activating the member failed. '
                            + 'Activate them manually from the member actions.',
                    });
                }
            }

            // A failed printer must not look like a failed promotion — the charge is already committed.
            try {
                await processPromotionReceipt(applied, preview.posItemName, receiptPrinterService,
                    status, member.name, member.membershipId);
            } catch {
                notification.warning({
                    message: 'Receipt not printed',
                    description: 'The promotion was applied, but the receipt could not be printed.',
                });
            }

            closeModal();
            onApplied?.();
        } catch {
            // Stay on the confirmation step so the operator can retry rather than re-enter everything.
        } finally {
            setIsProcessing(false);
        }
    };

    if (!canReadPromotions) {
        return null;
    }

    const priorNote = mostRecentApplication(history);
    const priorApplication = selectedPromotion
        ? lastApplicationOfPromotion(history, selectedPromotion.id)
        : null;

    return (
        <>
            <Button block={block} onClick={() => setOpen(true)} data-testid="member-add-promotion-btn">
                Add Promotion
            </Button>
            <Modal
                title={step === 'edit' ? 'Add Promotion' : 'Confirm promotion'}
                open={open}
                onCancel={closeModal}
                destroyOnClose
                footer={step === 'edit' ? [
                    <Button key="cancel" onClick={closeModal}>Cancel</Button>,
                    <Button
                        key="continue"
                        type="primary"
                        loading={isPreviewing}
                        disabled={selectedPromotionId == null || loadFailed}
                        onClick={handleContinue}
                        data-testid="promotion-continue-btn"
                    >
                        Continue
                    </Button>,
                ] : [
                    <Button
                        key="back"
                        disabled={isProcessing}
                        onClick={() => setStep('edit')}
                        data-testid="promotion-back-btn"
                    >
                        Back
                    </Button>,
                    <Button
                        key="confirm"
                        type="primary"
                        loading={isProcessing}
                        onClick={handleConfirm}
                        data-testid="promotion-confirm-btn"
                    >
                        {paymentStatus() === 'Completed' ? 'Confirm & take payment' : 'Confirm'}
                    </Button>,
                ]}
            >
                {/* The form stays mounted across both steps so Back preserves what was typed. */}
                <div style={{ display: step === 'edit' ? 'block' : 'none' }}>
                    {loadFailed && (
                        <Alert
                            style={{ marginBottom: 16 }}
                            type="error"
                            showIcon
                            data-testid="promotion-load-error"
                            message="This promotion couldn't be loaded"
                            description={
                                'The promotion list or the membership categories are unavailable '
                                + 'for this account, so a promotion cannot be chosen. If this keeps '
                                + 'happening, ask an administrator to check your permissions.'
                            }
                        />
                    )}
                    {priorNote && (
                        <Alert
                            style={{ marginBottom: 16 }}
                            type="info"
                            showIcon
                            message={`A promotion was applied on ${dayjs(priorNote.appliedOn).format(DATE_FORMAT)}: `
                                + `${priorNote.promotionName} (${priorNote.promotionType}).`}
                        />
                    )}
                    <div style={{ marginBottom: 16 }}>
                        <label htmlFor="add-promotion-select" style={{ display: 'block', marginBottom: 4 }}>
                            Promotion
                        </label>
                        <Select
                            id="add-promotion-select"
                            style={{ width: '100%' }}
                            placeholder="Select a promotion"
                            value={selectedPromotionId ?? undefined}
                            onChange={handlePromotionSelect}
                            options={promotions.map((p) => ({ label: `${p.name} (${p.promotionType})`, value: p.id }))}
                        />
                    </div>
                    <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>
                        {activeSpec?.renderFields({ categories: categoryOptions, currencySymbol })}
                        {/* Payment status is on the shared apply contract, not type-specific, so the
                            modal owns it rather than a type's renderFields. */}
                        {activeSpec && (
                            <Form.Item name="paymentStatus" label="Payment status" initialValue="Completed">
                                <RadioGroup
                                    optionType="button"
                                    buttonStyle="solid"
                                    data-testid="promotion-payment-status"
                                    options={[
                                        { label: 'Paid', value: 'Completed' },
                                        { label: 'Unpaid', value: 'Unpaid' },
                                    ]}
                                />
                            </Form.Item>
                        )}
                    </Form>
                </div>

                {step === 'confirm' && preview && (
                    <>
                        <PromotionConfirmationPanel
                            preview={preview}
                            memberName={member.name}
                            paymentStatus={paymentStatus()}
                            priorApplication={priorApplication}
                            extras={activeSpec?.renderConfirmationExtras?.(preview)}
                        />
                        {/* Offer to activate an inactive member as part of applying the promotion —
                            the common onboarding flow (take the promotion, then turn the member on). */}
                        {!member.isEnabled && (
                            <Checkbox
                                checked={activateMember}
                                onChange={(e) => setActivateMember(e.target.checked)}
                                data-testid="promotion-activate-member"
                                style={{ marginTop: 16 }}
                            >
                                Activate this member now
                            </Checkbox>
                        )}
                    </>
                )}
            </Modal>
        </>
    );
};

export default AddPromotionModal;
